import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type { BillingResponse, FirmRiskResponse } from '@thinkalpha/locates-ws-client';
import type { ConcreteAccount } from '@thinkalpha/trading-ws-client/contracts/account.js';
import type { FirmMembershipWithProfile, FirmWithParentsAndChildren } from '@thinkalpha/ui-types/types/admin/firms';
import type { Group } from '@thinkalpha/ui-types/types/admin/groups';
import type { ListPoolsResponse } from '@thinkalpha/ui-types/types/admin/lab-pools';
import type { Provider } from 'src/admin/LAB/contracts/providers';
import type { Route } from 'src/admin/LAB/contracts/routes';
import { observeOutcome } from 'src/admin/lib/queryOutcome';
import type { Outcome } from 'src/admin/lib/queryOutcome';
import { getFirmAccountsQuery } from 'src/queries/accounts';
import { getFirmGroupsQuery, getFirmMembersQuery, getFirmQuery } from 'src/queries/firms';
import { getAllFirmBillingRulesQuery } from 'src/queries/labBilling';
import { getFirmPoolsQuery } from 'src/queries/labPools';
import { getAllProvidersQuery } from 'src/queries/labProviders';
import { getFirmRiskQuery } from 'src/queries/labRisk';
import { getAllRoutesQuery } from 'src/queries/labRoutes';
import type { ReactBindings } from 'src/types/bindings';

/** What every endpoint said about one firm. */
export interface FirmDataOutcomes {
    /** `GET /firms/:id`. */
    firm: Outcome<FirmWithParentsAndChildren>;
    /** `GET /firms/:id/members`. */
    members: Outcome<FirmMembershipWithProfile[]>;
    /** `GET /groups?firmId=`. */
    groups: Outcome<Group[]>;
    /** trading-ws `GET /accounts?firmId=`. */
    accounts: Outcome<ConcreteAccount[]>;
    /** locates-ws `GET /v1/risk/firm/:id`; 404 means the firm is not enrolled. */
    risk: Outcome<FirmRiskResponse>;
    /** locates-ws `GET /v1/providers?firmId=`. */
    providers: Outcome<Provider[]>;
    /** locates-ws `GET /v1/routes?firmId=`. */
    routes: Outcome<Route[]>;
    /** locates-ws `GET /v1/inventory/pools?firmId=`. */
    pools: Outcome<ListPoolsResponse>;
    /** locates-ws `GET /v1/billing/firm/:id`. */
    billing: Outcome<BillingResponse>;
}

/**
 * The queries behind one firm's facts. Everything past `GET /firms/:id` waits for it, so a firm the
 * viewer cannot open costs one refused request instead of nine.
 */
export class FirmData implements Disposable {
    readonly #stack = new DisposableStack();
    /** `GET /firms/:id` alone, for callers that only need to know who the firm's relations are. */
    readonly firm: ReadonlySignal<Outcome<FirmWithParentsAndChildren>>;
    readonly outcomes: ReadonlySignal<FirmDataOutcomes>;

    /**
     * @param scope Only the focal firm's groups reach the page, so related firms skip that request
     * and report it as pending.
     */
    constructor(
        readonly firmId: string,
        queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        scope: 'focal' | 'related' = 'focal',
    ) {
        const observe = <T>(options: ReadonlySignal<QueryObserverOptions<T, Error, T>>) =>
            observeOutcome(this.#stack, queryObserverModelFactory, options);

        const firm = observe(computed(() => getFirmQuery(firmId)));
        this.firm = firm;
        const open = computed(() => firm.value.status === 'ok');
        const afterFirm = <T>(options: QueryObserverOptions<T, Error, T>) =>
            observe(computed(() => ({ ...options, enabled: open.value })));

        const members = afterFirm(getFirmMembersQuery(firmId));
        const groups: ReadonlySignal<Outcome<Group[]>> =
            scope === 'focal' ? afterFirm(getFirmGroupsQuery(firmId)) : signal({ status: 'pending' });
        const accounts = afterFirm(getFirmAccountsQuery(firmId));
        const risk = afterFirm(getFirmRiskQuery(firmId));
        const providers = afterFirm(getAllProvidersQuery(firmId));
        const routes = afterFirm(getAllRoutesQuery(firmId));
        const pools = afterFirm(getFirmPoolsQuery(firmId));
        const billing = afterFirm(getAllFirmBillingRulesQuery(firmId, {}));

        this.outcomes = computed(() => ({
            firm: firm.value,
            members: members.value,
            groups: groups.value,
            accounts: accounts.value,
            risk: risk.value,
            providers: providers.value,
            routes: routes.value,
            pools: pools.value,
            billing: billing.value,
        }));
    }

    [Symbol.dispose](): void {
        this.#stack.dispose();
    }
}
